import {
	SET_CUSTOMER_CONVERSATIONS,
	ADD_CUSTOMER_CONVERSATION,
	SET_ACTIVE_CONVERSATION,
	UPDATE_CUSTOMER_CONVERSATION,
	UPSERT_CUSTOMER_CONVERSATION,
	REMOVE_CUSTOMER_CONVERSATION,
	SET_CUSTOMER_CONVERSATION_MESSAGES,
	ADD_CUSTOMER_CONVERSATION_MESSAGE,
	LOADING,
	LOADING_MORE_CUSTOMER_CONVERSATIONS,
	OPEN_CONVERSATIONS_SELECTED,
	SET_CUSTOMER_CONVERSATION_ASSIGNED_TYPE,
	SHOW_ONLY_UNREAD_CONVERSATIONS,
	SET_CUSTOMER_CONVERSATION_CHANNEL_FILTER,
	SET_CUSTOMER_CONVERSATIONS_LAST_SYNCED_AT,
	SET_SOCKET_ID,
	ACTIVE_DASHBOARD,
	ADD_OPTIMISTIC_MESSAGE,
	FAIL_OPTIMISTIC_MESSAGE,
	REMOVE_OPTIMISTIC_MESSAGE,
	SET_COMPOSER_RESTORE,
	CLEAR_COMPOSER_RESTORE
} from '@/actions/types';

const initialState = {
	customerConversations: [],
	assignedTypeSelected: 'ALL',
	openConversationsSelected: true,
	showOnlyUnreadConversations: false,
	channelFilter: undefined,
	lastCustomerConversationsSyncedAt: null,
	activeConversation: {},
	loading: true,
	activeDashboard: 'webchat',
	// Payload consumed by ChatBox when the user clicks a failed bubble to edit/retry.
	pendingComposerRestore: null
};

export default function customerConversationReducer(
	state = initialState,
	action
) {
	switch (action.type) {
		case 'CUSTOMER_PHOTO_SAVED': {
			const patchCustomer = (customer) => customer?._id === action.payload._id
				? { ...customer, photoUrl: action.payload.photoUrl }
				: customer;
			const patchConversation = (conversation) => ({
				...conversation,
				...(conversation._customer && { _customer: patchCustomer(conversation._customer) }),
				...(conversation._customers && { _customers: conversation._customers.map(patchCustomer) }),
			});
			return {
				...state,
				customerConversations: state.customerConversations.map(patchConversation),
				activeConversation: patchConversation(state.activeConversation),
			};
		}
		case SET_CUSTOMER_CONVERSATIONS:
			return {
				...state,
				customerConversations: action.payload
			};
		case SET_SOCKET_ID:
			return {
				...state,
				socketId: action.payload
			};
		case ADD_CUSTOMER_CONVERSATION:
			return {
				...state,
				customerConversations: [
					...state.customerConversations,
					action.payload
				]
			};
		case UPDATE_CUSTOMER_CONVERSATION:
			return {
				...state,
				customerConversations: state.customerConversations.map(
					(convo) =>
						convo._id === action.payload._id
							? action.payload
							: convo
				),
				// Also update activeConversation if it's the one being updated
				activeConversation:
					state.activeConversation._id === action.payload._id
						? action.payload
						: state.activeConversation
			};
	// add conversation if it doesn't exist, otherwise update the existing conversation
	// _assignees (array) is primary, _assignee (single) is deprecated
	case UPSERT_CUSTOMER_CONVERSATION: {
		const existingConvo = state.customerConversations.find(
			(convo) => convo._id === action.payload._id
		);

		// Determine if this is an intentional unassign (all assignment fields cleared)
		// vs stale data (missing _assignees field due to schema not having it)
		const isIntentionalUnassign =
			action.payload._assignees !== undefined &&
			action.payload._assignees.length === 0 &&
			!action.payload._department &&
			!action.payload._assignee;

		// Merge logic:
		// - Use incoming _assignees if it has items (real assignment data)
		// - Use incoming _assignees if it's an intentional unassign (all fields cleared)
		// - Otherwise preserve local _assignees (stale websocket data without _assignees)
		const mergedConversation = existingConvo
			? {
					...action.payload,
					_assignees:
						action.payload._assignees?.length > 0
							? action.payload._assignees
							: isIntentionalUnassign
								? []
								: existingConvo._assignees || [],
					_department:
						action.payload._department !== undefined
							? action.payload._department
							: existingConvo._department,
					// _assignee is deprecated - use incoming if provided, otherwise local
					_assignee:
						action.payload._assignee !== undefined
							? action.payload._assignee
							: existingConvo._assignee
				}
			: action.payload;

			return {
				...state,
				customerConversations: existingConvo
					? state.customerConversations.map((convo) =>
							convo._id === action.payload._id
								? mergedConversation
								: convo
						)
					: [...state.customerConversations, action.payload],
				// Also update activeConversation if it's the one being updated
				activeConversation:
					state.activeConversation._id === action.payload._id
						? mergedConversation
						: state.activeConversation
			};
		}
		case REMOVE_CUSTOMER_CONVERSATION:
			return {
				...state,
				customerConversations: state.customerConversations.filter(
					(convo) => convo._id !== action.payload
				)
			};

		case SET_CUSTOMER_CONVERSATION_MESSAGES:
			return {
				...state,
				customerConversations: state.customerConversations.map(
					(convo) =>
						convo._id === action.payload._id
							? action.payload
							: convo
				)
			};
		case ADD_CUSTOMER_CONVERSATION_MESSAGE:
			return {
				...state,
				customerConversations: state.customerConversations.map(
					(convo) =>
						convo._id === action.payload._id
							? action.payload
							: convo
				)
			};
		case SET_ACTIVE_CONVERSATION:
			return {
				...state,
				activeConversation:
					state.customerConversations.find(
						(convo) => convo._id === action.payload
					) || {}
			};

		case OPEN_CONVERSATIONS_SELECTED:
			return {
				...state,
				openConversationsSelected: action.payload
			};
		case SET_CUSTOMER_CONVERSATION_ASSIGNED_TYPE:
			return {
				...state,
				assignedTypeSelected: action.payload
			};
		case SHOW_ONLY_UNREAD_CONVERSATIONS:
			return {
				...state,
				showOnlyUnreadConversations: action.payload
			};
		case SET_CUSTOMER_CONVERSATION_CHANNEL_FILTER:
			return {
				...state,
				channelFilter: action.payload
			};
		case SET_CUSTOMER_CONVERSATIONS_LAST_SYNCED_AT:
			return {
				...state,
				lastCustomerConversationsSyncedAt: action.payload
			};
		case LOADING_MORE_CUSTOMER_CONVERSATIONS:
			return {
				...state,
				loadingMoreConversations: action.payload
			};
		case LOADING:
			return {
				...state,
				loading: action.payload
			};
		case ACTIVE_DASHBOARD:
			return {
				...state,
				activeDashboard: action.payload
			};

		case ADD_OPTIMISTIC_MESSAGE: {
			const { _conversation, message } = action.payload;
			const applyInsert = (convo) => {
				if (!convo || convo._id !== _conversation) return convo;
				const messages = Array.isArray(convo.messages) ? convo.messages : [];
				if (messages.some((m) => m && m.clientId === message.clientId)) {
					return convo;
				}
				// notes don't count as activity, so they don't move the conversation up
				const latestMessageDate =
					message.sender === 'note'
						? convo.latestMessageDate
						: message.createdAt || new Date().toISOString();
				return {
					...convo,
					latestMessageDate,
					messages: [...messages, message]
				};
			};
			return {
				...state,
				customerConversations: state.customerConversations.map(applyInsert),
				activeConversation:
					state.activeConversation && state.activeConversation._id === _conversation
						? applyInsert(state.activeConversation)
						: state.activeConversation
			};
		}

		case FAIL_OPTIMISTIC_MESSAGE: {
			const { _conversation, clientId, errorMessage } = action.payload;
			const applyFail = (convo) => {
				if (!convo || convo._id !== _conversation) return convo;
				const messages = Array.isArray(convo.messages) ? convo.messages : [];
				return {
					...convo,
					messages: messages.map((m) =>
						m && m.clientId === clientId
							? { ...m, status: 'failed', errorMessage }
							: m
					)
				};
			};
			return {
				...state,
				customerConversations: state.customerConversations.map(applyFail),
				activeConversation:
					state.activeConversation && state.activeConversation._id === _conversation
						? applyFail(state.activeConversation)
						: state.activeConversation
			};
		}

		case REMOVE_OPTIMISTIC_MESSAGE: {
			const { _conversation, clientId } = action.payload;
			const applyRemove = (convo) => {
				if (!convo || convo._id !== _conversation) return convo;
				const messages = Array.isArray(convo.messages) ? convo.messages : [];
				return {
					...convo,
					messages: messages.filter((m) => !m || m.clientId !== clientId)
				};
			};
			return {
				...state,
				customerConversations: state.customerConversations.map(applyRemove),
				activeConversation:
					state.activeConversation && state.activeConversation._id === _conversation
						? applyRemove(state.activeConversation)
						: state.activeConversation
			};
		}

		case SET_COMPOSER_RESTORE:
			return {
				...state,
				pendingComposerRestore: action.payload
			};

		case CLEAR_COMPOSER_RESTORE:
			return {
				...state,
				pendingComposerRestore: null
			};

		default:
			return state;
	}
}
